import { capitaliseFirst } from "@./string-utils";
import {
  PRICING_PLAN_CADENCE,
  PRICING_PLAN_QUANTITY,
} from "@/constants/pricing";
import { TraySize } from "@/constants/products";
import { PricingPlan } from "@/primary-models";

const formatTitle = (pricingPlan: PricingPlan) => `${capitaliseFirst(pricingPlan, true)} plan`;
const BEST_VALUE_TAG = "Best value";

const formatPricePerDay = (totalPricePounds: number, cadenceDays: number) => {
  // Avoid float rounding issues by working in pence
  const totalPence = Math.round(totalPricePounds * 100);
  const perDayPence = Math.round(totalPence / cadenceDays);
  return `£${(perDayPence / 100).toFixed(2)}/day`;
};

const makeMealPlan = ({
  pricingPlan,
  totalPricePounds,
  tag,
}:{
  pricingPlan: PricingPlan;
  totalPricePounds: number;
  tag?: string;
}) => {
  const cadenceDays = PRICING_PLAN_CADENCE[pricingPlan];
  const quantityDays = PRICING_PLAN_QUANTITY[pricingPlan];

  return {
    _uid: pricingPlan,
    title: formatTitle(pricingPlan),
    value: pricingPlan,
    component: "select_option",
    advanced_options: {
      secondaryText: `${quantityDays} days worth of food every ${cadenceDays} days`,
      tertiaryText: formatPricePerDay(totalPricePounds, cadenceDays),
      ...(tag ? { tag } : {}),
    },
  } as const;
};

// Tray size A
export const MEAL_PLAN_STANDARD_A = makeMealPlan({
  pricingPlan: PricingPlan.STANDARD,
  totalPricePounds: 60.96,
});
export const MEAL_PLAN_SAVER_A = makeMealPlan({
  pricingPlan: PricingPlan.SAVER,
  totalPricePounds: 83.75,
  tag: BEST_VALUE_TAG,
});
export const MEAL_PLAN_HALF_A = makeMealPlan({
  pricingPlan: PricingPlan.HALF,
  totalPricePounds: 60.96,
});
export const MEAL_PLAN_FREEZER_A = makeMealPlan({
  pricingPlan: PricingPlan.FREEZER,
  totalPricePounds: 32.88,
});

// Tray size B
export const MEAL_PLAN_STANDARD_B = makeMealPlan({
  pricingPlan: PricingPlan.STANDARD,
  totalPricePounds: 65.37,
});
export const MEAL_PLAN_SAVER_B = makeMealPlan({
  pricingPlan: PricingPlan.SAVER,
  totalPricePounds: 93.11,
  tag: BEST_VALUE_TAG,
});
export const MEAL_PLAN_HALF_B = makeMealPlan({
  pricingPlan: PricingPlan.HALF,
  totalPricePounds: 65.37,
});
export const MEAL_PLAN_FREEZER_B = makeMealPlan({
  pricingPlan: PricingPlan.FREEZER,
  totalPricePounds: 34.89,
});

// Tray size C
export const MEAL_PLAN_STANDARD_C = makeMealPlan({
  pricingPlan: PricingPlan.STANDARD,
  totalPricePounds: 68.95,
});
export const MEAL_PLAN_SAVER_C = makeMealPlan({
  pricingPlan: PricingPlan.SAVER,
  totalPricePounds: 98.06,
  tag: BEST_VALUE_TAG,
});
export const MEAL_PLAN_HALF_C = makeMealPlan({
  pricingPlan: PricingPlan.HALF,
  totalPricePounds: 68.95,
});
export const MEAL_PLAN_FREEZER_C = makeMealPlan({
  pricingPlan: PricingPlan.FREEZER,
  totalPricePounds: 36.99,
});

// Tray size D
export const MEAL_PLAN_STANDARD_D = makeMealPlan({
  pricingPlan: PricingPlan.STANDARD,
  totalPricePounds: 72.5,
});
export const MEAL_PLAN_SAVER_D = makeMealPlan({
  pricingPlan: PricingPlan.SAVER,
  totalPricePounds: 103.16,
  tag: BEST_VALUE_TAG,
});
export const MEAL_PLAN_HALF_D = makeMealPlan({
  pricingPlan: PricingPlan.HALF,
  totalPricePounds: 72.5,
});
export const MEAL_PLAN_FREEZER_D = makeMealPlan({
  pricingPlan: PricingPlan.FREEZER,
  totalPricePounds: 39.08,
});

// Tray size E
export const MEAL_PLAN_STANDARD_E = makeMealPlan({
  pricingPlan: PricingPlan.STANDARD,
  totalPricePounds: 75.81,
});
export const MEAL_PLAN_SAVER_E = makeMealPlan({
  pricingPlan: PricingPlan.SAVER,
  totalPricePounds: 106.48,
  tag: BEST_VALUE_TAG,
});
export const MEAL_PLAN_HALF_E = makeMealPlan({
  pricingPlan: PricingPlan.HALF,
  totalPricePounds: 75.81,
});
export const MEAL_PLAN_FREEZER_E = makeMealPlan({
  pricingPlan: PricingPlan.FREEZER,
  totalPricePounds: 40.66,
});

// Tray size F
export const MEAL_PLAN_STANDARD_F = makeMealPlan({
  pricingPlan: PricingPlan.STANDARD,
  totalPricePounds: 79.03,
});
export const MEAL_PLAN_SAVER_F = makeMealPlan({
  pricingPlan: PricingPlan.SAVER,
  totalPricePounds: 109.81,
  tag: BEST_VALUE_TAG,
});
export const MEAL_PLAN_HALF_F = makeMealPlan({
  pricingPlan: PricingPlan.HALF,
  totalPricePounds: 79.03,
});
export const MEAL_PLAN_FREEZER_F = makeMealPlan({
  pricingPlan: PricingPlan.FREEZER,
  totalPricePounds: 41.97,
});

export const ALL_MEAL_PLANS_TRAY_SIZE_A = [
  MEAL_PLAN_STANDARD_A,
  MEAL_PLAN_SAVER_A,
  MEAL_PLAN_HALF_A,
  // FREEZER is not available for all customers, only for those who have limited freezer space
  // MEAL_PLAN_FREEZER,
];

export const ALL_MEAL_PLANS_TRAY_SIZE_B = [
  MEAL_PLAN_STANDARD_B,
  MEAL_PLAN_SAVER_B,
  MEAL_PLAN_HALF_B,
  // FREEZER is not available for all customers, only for those who have limited freezer space
  // MEAL_PLAN_FREEZER_B,
];

export const ALL_MEAL_PLANS_TRAY_SIZE_C = [
  MEAL_PLAN_STANDARD_C,
  MEAL_PLAN_SAVER_C,
  MEAL_PLAN_HALF_C,
  // FREEZER is not available for all customers, only for those who have limited freezer space
  // MEAL_PLAN_FREEZER_C,
];

export const ALL_MEAL_PLANS_TRAY_SIZE_D = [
  MEAL_PLAN_STANDARD_D,
  MEAL_PLAN_SAVER_D,
  MEAL_PLAN_HALF_D,
  // FREEZER is not available for all customers, only for those who have limited freezer space
  // MEAL_PLAN_FREEZER_D,
];

export const ALL_MEAL_PLANS_TRAY_SIZE_E = [
  MEAL_PLAN_STANDARD_E,
  MEAL_PLAN_SAVER_E,
  MEAL_PLAN_HALF_E,
  // FREEZER is not available for all customers, only for those who have limited freezer space
  // MEAL_PLAN_FREEZER_E,
];

export const ALL_MEAL_PLANS_TRAY_SIZE_F = [
  MEAL_PLAN_STANDARD_F,
  MEAL_PLAN_SAVER_F,
  MEAL_PLAN_HALF_F,
  // FREEZER is not available for all customers, only for those who have limited freezer space
  // MEAL_PLAN_FREEZER_F,
];

export const allMealPlansByTraySize = {
  [TraySize.A]: ALL_MEAL_PLANS_TRAY_SIZE_A,
  [TraySize.B]: ALL_MEAL_PLANS_TRAY_SIZE_B,
  [TraySize.C]: ALL_MEAL_PLANS_TRAY_SIZE_C,
  [TraySize.D]: ALL_MEAL_PLANS_TRAY_SIZE_D,
  [TraySize.E]: ALL_MEAL_PLANS_TRAY_SIZE_E,
  [TraySize.F]: ALL_MEAL_PLANS_TRAY_SIZE_F,
};

export const halfMealPlansByTraySize = {
  [TraySize.A]: MEAL_PLAN_HALF_A,
  [TraySize.B]: MEAL_PLAN_HALF_B,
  [TraySize.C]: MEAL_PLAN_HALF_C,
  [TraySize.D]: MEAL_PLAN_HALF_D,
  [TraySize.E]: MEAL_PLAN_HALF_E,
  [TraySize.F]: MEAL_PLAN_HALF_F,
};

export const saverMealPlansByTraySize = {
  [TraySize.A]: MEAL_PLAN_SAVER_A,
  [TraySize.B]: MEAL_PLAN_SAVER_B,
  [TraySize.C]: MEAL_PLAN_SAVER_C,
  [TraySize.D]: MEAL_PLAN_SAVER_D,
  [TraySize.E]: MEAL_PLAN_SAVER_E,
  [TraySize.F]: MEAL_PLAN_SAVER_F,
};

export const freezerMealPlansByTraySize = {
  [TraySize.A]: MEAL_PLAN_FREEZER_A,
  [TraySize.B]: MEAL_PLAN_FREEZER_B,
  [TraySize.C]: MEAL_PLAN_FREEZER_C,
  [TraySize.D]: MEAL_PLAN_FREEZER_D,
  [TraySize.E]: MEAL_PLAN_FREEZER_E,
  [TraySize.F]: MEAL_PLAN_FREEZER_F,
};
